import { useEffect, useRef, useState } from "react";
import { supabase, supabaseConfigured } from "./client";
import { pullAll, pushAll, isDemoDataset, isDemoSprintId, isDemoRoadmapId, deleteSprintFromCloud, mergeRemoteLocal, asTs } from "./sync";
import { isNativeIOS } from "../lib/platform";
import { reconcileEntriesDerivedMetrics } from "../domain/entries";
import type { GEntry, GSprint } from "../types/entries";

type Status = "idle" | "syncing" | "error";

type OverwriteWarning = {
  localEntries: number;
  localSprints: number;
  cloudEntries: number;
  cloudSprints: number;
};

type Args = {
  userId: string | null;
  initialDataLoaded: boolean;
  entries: any[];
  sprints: any[];
  vitals: any;
  forecast: any;
  roadmap: any | null;
  roadmapUpdatedAt: string | null;
  setEntries: (v: any[]) => void;
  setSprints: (v: any[]) => void;
  setVitals: (v: any) => void;
  setForecast: (updater: any) => void;
  setRoadmap: (v: any | null) => void;
  setRoadmapUpdatedAt: (v: string | null) => void;
  persistAll: (entries: any[], vitals: any, forecast: any, sprints: any[], opts?: any) => Promise<void>;
  isYmd: (v: string) => boolean;
  toYmdLoose: (v: any) => string;
  enabled?: boolean;
  isDemoMode?: boolean;
  deletedEntryTombstones?: Record<string, string>;
  hasSynced?: boolean;
  onSyncSuccess?: () => Promise<void>;
  setHealthKitEnabled?: (v: boolean, cloudProfileUpdatedAt?: string | null) => void;
};

export function useSupabaseSync(args: Args) {
  const {
    userId,
    initialDataLoaded,
    entries,
    sprints,
    vitals,
    forecast,
    roadmap,
    roadmapUpdatedAt,
    setEntries,
    setSprints,
    setVitals,
    setForecast,
    setRoadmap,
    setRoadmapUpdatedAt,
    persistAll,
    isYmd,
    toYmdLoose,
    enabled = true,
    isDemoMode = false,
    deletedEntryTombstones = {},
    hasSynced = false,
    onSyncSuccess,
    setHealthKitEnabled,
  } = args;

  const [status, setStatus] = useState<Status>("idle");
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Tracks whether the initial cloud pull (on login) has completed at least once.
  // Starts true when there is no userId or Supabase isn't configured (no pull will happen).
  const [firstPullDone, setFirstPullDone] = useState<boolean>(!supabaseConfigured || !userId);

  // Prevents push from firing right after a pull
  const lastPulledAtRef = useRef<number>(0);
  const pushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingHydrationRef = useRef<null | { entries: any[]; sprints: any[]; vitals: any; forecast: any; roadmap: any | null; profileUpdatedAt: string | null }>(null);
  const [overwriteWarning, setOverwriteWarning] = useState<OverwriteWarning | null>(null);
  const [cloudProfileVitalsStatus, setCloudProfileVitalsStatus] = useState<"unknown" | "present" | "absent">("unknown");

  const hasMeaningfulLocalData = () => {
    const hasVitals = !!(vitals && typeof vitals === "object" && (
      (typeof vitals.gender === "string" && vitals.gender.trim())
      || (typeof vitals.birthday === "string" && vitals.birthday.trim())
      || Number.isFinite(vitals.height)
      || Number.isFinite(vitals.currentWeightLbs)
    ));
    return entries.length > 0 || sprints.length > 0 || !!roadmap || hasVitals;
  };

  const snapshotSignature = (vals: { entriesCount: number; sprintsCount: number; latestEntryDate: string; latestSprintUpdate: string }) =>
    `${vals.entriesCount}:${vals.sprintsCount}:${vals.latestEntryDate}:${vals.latestSprintUpdate}`;

  useEffect(() => {
    if (!enabled || isDemoMode || !supabaseConfigured || !userId) {
      setCloudProfileVitalsStatus("unknown");
      setFirstPullDone(true);
      return;
    }
    setCloudProfileVitalsStatus("unknown");
    setFirstPullDone(false);
  }, [enabled, isDemoMode, userId]);

  // Cancel any pending debounced push immediately when demo mode starts.
  // Defense-in-depth: the debounced push effect's cleanup also clears the timer,
  // but this explicit effect fires synchronously on the same render cycle.
  useEffect(() => {
    if (!isDemoMode) return;
    if (pushTimerRef.current) {
      clearTimeout(pushTimerRef.current);
      pushTimerRef.current = null;
    }
  }, [isDemoMode]);

  // Pull on login (once initialDataLoaded is also true)
  useEffect(() => {
    if (!enabled || isDemoMode || !userId || !initialDataLoaded || firstPullDone || overwriteWarning) return;
    pullAndMerge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isDemoMode, userId, initialDataLoaded, firstPullDone, overwriteWarning]);

  // Debounced push on data changes (skip right after a pull)
  useEffect(() => {
    if (!enabled || isDemoMode || !userId || !initialDataLoaded || !firstPullDone) return;

    if (pushTimerRef.current) clearTimeout(pushTimerRef.current);
    pushTimerRef.current = setTimeout(() => {
      // Skip push if a pull happened in the last 3s (avoids push-after-pull loop)
      if (Date.now() - lastPulledAtRef.current < 3000) return;
      runPush();
    }, 1500);

    return () => {
      if (pushTimerRef.current) clearTimeout(pushTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isDemoMode, entries, sprints, vitals, forecast, roadmap, userId, initialDataLoaded, firstPullDone]);

  const pullAndMerge = async () => {
    if (!enabled || isDemoMode || !userId || pendingHydrationRef.current) return;
    setStatus("syncing");
    setError(null);
    try {
      const remote = await pullAll(userId);
      lastPulledAtRef.current = Date.now();
      if (remote.hasProfile && typeof remote.healthKitEnabled === "boolean") {
        console.info("[healthkit] startup resolution: using cloud profile value", { value: remote.healthKitEnabled });
        setHealthKitEnabled?.(remote.healthKitEnabled, remote.profileUpdatedAt);
      } else {
        console.info("[healthkit] startup resolution: cloud value missing, keeping local value");
      }

      // Merge local offline changes with remote data using timestamp-based conflict resolution.
      // Local changes win when they have a newer updatedAt; remote wins on tie or missing timestamp.
      const remoteEntries = remote.entries.map((e) => ({ ...e, date: toYmdLoose((e as any)?.date) }));
      const remoteEntriesFiltered = remoteEntries.filter((e: any) => isYmd(String(e?.date ?? "")));

      // Merge: local + remote by date, remote wins on tie
      const mergedBeforeTombstones = mergeRemoteLocal(
        entries as any[],
        remoteEntriesFiltered,
        (e: any) => String(e.date),
        (e: any) => (e as any).updatedAt ?? null,
      );

      // Apply tombstones: suppress entries that were deleted offline after their last cloud update
      const mergedRaw = mergedBeforeTombstones.filter((e: any) => {
        const tombstoneTs = deletedEntryTombstones[e.date];
        if (!tombstoneTs) return true; // not deleted locally
        const entryTs = asTs((e as any).updatedAt);
        const delTs = asTs(tombstoneTs);
        // Keep the entry only if the remote updated it AFTER the local deletion
        return entryTs !== null && delTs !== null && entryTs > delTs;
      }).sort((a: any, b: any) => (a.date < b.date ? -1 : 1));

      const preMergeHasDemo = mergedRaw.some((e: any) => String(e?.tdeeSource ?? "") === "demo");

      // For authenticated sessions, hydrate app state from cloud only.
      // nextVitals must be computed before reconcileEntriesDerivedMetrics so it can be passed as the
      // required second argument — otherwise vitals is undefined and crashes on currentWeightLbs.
      // On the first pull (firstPullDone is still false), do NOT fall back to stale local React-state
      // vitals: clearAnonymousLocalState() already wiped localStorage on SIGNED_IN, but the React state
      // hasn't been reset yet, so `vitals` may still hold a previous local user's data.
      const nextVitals = remote.vitals ?? (firstPullDone ? vitals : null);
      setCloudProfileVitalsStatus(remote.hasProfileVitals ? "present" : "absent");
      const nextForecast = remote.forecast ?? forecast;
      const remoteRoadmap = Array.isArray(remote.roadmaps) && remote.roadmaps.length > 0 ? remote.roadmaps[0] : null;
      const nextRoadmap = remoteRoadmap ?? null;

      const mergedEntries = reconcileEntriesDerivedMetrics(mergedRaw as any[], nextVitals ?? {});

      const mergedSprints = mergeRemoteLocal(
        sprints as any[],
        remote.sprints.filter((s: any) => !!s.id && isYmd(String(s.startDate ?? ""))),
        (s: any) => String(s.id),
        (s: any) => s.updatedAt ?? null,
      ).filter((s: any) => !!s.id && isYmd(String(s.startDate ?? "")));

      // Defense-in-depth: never allow demo-shaped data into authenticated state.
      // When detected, discard local state entirely and use only cloud data.
      if (isDemoMode || preMergeHasDemo || isDemoDataset(mergedEntries as (GEntry & { tdeeSource?: string | null })[], mergedSprints as GSprint[], nextRoadmap ? [nextRoadmap] : [])) {
        console.warn("[sync] Demo/suspect dataset detected in local state — using cloud-only data.");
        const cleanEntries = reconcileEntriesDerivedMetrics(
          remoteEntriesFiltered.filter((e: any) => String(e?.tdeeSource ?? "") !== "demo") as any[],
          nextVitals ?? {},
        );
        const cleanSprints = remote.sprints
          .filter((s: any) => !!s.id && isYmd(String(s.startDate ?? "")) && !isDemoSprintId(String(s.id)));
        const cleanRoadmap = (nextRoadmap && !isDemoRoadmapId(String(nextRoadmap?.id ?? ""))) ? nextRoadmap : null;
        const cleanForecast = remote.forecast ?? forecast;

        setEntries(cleanEntries);
        setSprints(cleanSprints);
        setVitals(nextVitals);
        setForecast(cleanForecast);
        if (cleanRoadmap !== roadmap) setRoadmap(cleanRoadmap);
        if (remote.profileUpdatedAt) setRoadmapUpdatedAt(String(remote.profileUpdatedAt));
        await persistAll(cleanEntries, nextVitals, cleanForecast, cleanSprints, { skipRemoteSync: true });
        await onSyncSuccess?.();
        setLastSyncAt(new Date().toISOString());
        setStatus("idle");
        setFirstPullDone(true);
        return;
      }

      const cloudHasData = mergedEntries.length > 0 || mergedSprints.length > 0 || !!nextRoadmap || !!nextVitals;
      // Only warn if local has actual weight entries or sprints — vitals-only local state
      // (which may be stale React state after clearAnonymousLocalState clears localStorage)
      // should never block a clean cloud hydration.
      const localHasEntries = entries.length > 0 || sprints.length > 0;
      const localSig = snapshotSignature({
        entriesCount: entries.length,
        sprintsCount: sprints.length,
        latestEntryDate: String(entries[entries.length - 1]?.date ?? ""),
        latestSprintUpdate: String(sprints[sprints.length - 1]?.updatedAt ?? sprints[sprints.length - 1]?.startDate ?? ""),
      });
      const cloudSig = snapshotSignature({
        entriesCount: mergedEntries.length,
        sprintsCount: mergedSprints.length,
        latestEntryDate: String(mergedEntries[mergedEntries.length - 1]?.date ?? ""),
        latestSprintUpdate: String(mergedSprints[mergedSprints.length - 1]?.updatedAt ?? mergedSprints[mergedSprints.length - 1]?.startDate ?? ""),
      });

      // Only show the overwrite warning on the very first sync (when the user has never synced
      // before on this device). For returning users coming back online after offline edits, we
      // have already merged local + remote above so no destructive overwrite is needed.
      if (!isNativeIOS && !hasSynced && localHasEntries && cloudHasData && localSig !== cloudSig) {
        pendingHydrationRef.current = {
          entries: mergedEntries,
          sprints: mergedSprints,
          vitals: nextVitals,
          forecast: nextForecast,
          roadmap: nextRoadmap,
          profileUpdatedAt: remote.profileUpdatedAt ? String(remote.profileUpdatedAt) : null,
        };
        setOverwriteWarning({
          localEntries: entries.length,
          localSprints: sprints.length,
          cloudEntries: mergedEntries.length,
          cloudSprints: mergedSprints.length,
        });
        setStatus("idle");
        return;
      }

      setEntries(mergedEntries);
      setSprints(mergedSprints);
      setVitals(nextVitals);
      setForecast(nextForecast);
      if (nextRoadmap !== roadmap) setRoadmap(nextRoadmap);
      if (remote.profileUpdatedAt) setRoadmapUpdatedAt(String(remote.profileUpdatedAt));

      await persistAll(mergedEntries, nextVitals, nextForecast, mergedSprints, { skipRemoteSync: true });
      await onSyncSuccess?.();

      const now = new Date().toISOString();
      setLastSyncAt(now);
      setStatus("idle");
    } catch (err: any) {
      setCloudProfileVitalsStatus("unknown");
      setStatus("error");
      setError(err?.message ?? "Sync failed");
    } finally {
      setFirstPullDone(true);
    }
  };

  const runPush = async () => {
    if (!enabled || isDemoMode || !userId) return;
    // Second guard: detect demo-shaped data in case isDemoMode flag is stale
    const roadmaps = roadmap ? [roadmap] : [];
    if (isDemoDataset(entries as (GEntry & { tdeeSource?: string | null })[], sprints as GSprint[], roadmaps)) {
      console.warn("[sync] runPush blocked — demo dataset detected in local state.");
      setStatus("error");
      setError("Sync blocked: demo/suspect dataset detected. Exit demo and retry sync.");
      return;
    }
    setStatus("syncing");
    setError(null);
    try {
      await pushAll(userId, entries as (GEntry & { tdeeSource?: string | null })[], sprints as GSprint[], vitals, forecast, roadmaps);
      await onSyncSuccess?.();
      const now = new Date().toISOString();
      setRoadmapUpdatedAt(now);
      setLastSyncAt(now);
      setStatus("idle");
    } catch (err: any) {
      setStatus("error");
      setError(err?.message ?? "Push failed");
    }
  };

  const applyPendingHydration = async () => {
    const pending = pendingHydrationRef.current;
    if (!pending) return;
    setEntries(pending.entries);
    setSprints(pending.sprints);
    setVitals(pending.vitals);
    setForecast(pending.forecast);
    if (pending.roadmap !== roadmap) setRoadmap(pending.roadmap);
    if (pending.profileUpdatedAt) setRoadmapUpdatedAt(pending.profileUpdatedAt);
    await persistAll(pending.entries, pending.vitals, pending.forecast, pending.sprints, { skipRemoteSync: true });
    await onSyncSuccess?.();
    pendingHydrationRef.current = null;
    setOverwriteWarning(null);
    const now = new Date().toISOString();
    setLastSyncAt(now);
    setStatus("idle");
    setFirstPullDone(true);
  };

  const confirmOverwriteWithCloud = async () => {
    setStatus("syncing");
    setError(null);
    try {
      await applyPendingHydration();
    } catch (err: any) {
      setStatus("error");
      setError(err?.message ?? "Cloud hydrate failed");
    }
  };

  const cancelOverwriteAndKeepLocal = async () => {
    // User chose to keep their local data — push it to cloud so it becomes the source of truth.
    pendingHydrationRef.current = null;
    setOverwriteWarning(null);
    setFirstPullDone(true);
    // Trigger push of local state to cloud
    await runPush();
    await onSyncSuccess?.();
  };

  const deleteCloudSprint = async (sprintId: string) => {
    if (!enabled || isDemoMode || !userId) return;
    try {
      await deleteSprintFromCloud(userId, sprintId);
    } catch (err: any) {
      setStatus("error");
      setError(err?.message ?? "Failed to delete sprint from cloud");
    }
  };

  const syncNow = async () => {
    if (!enabled || isDemoMode) return;
    await pullAndMerge();
  };

  // Expose signOut so Settings can call it without importing supabase directly
  const signOut = () => (enabled && !isDemoMode && supabaseConfigured) ? supabase.auth.signOut() : Promise.resolve({ error: null });

  return { status, lastSyncAt, error, syncNow, signOut, firstPullDone, overwriteWarning, confirmOverwriteWithCloud, cancelOverwriteAndKeepLocal, deleteCloudSprint, cloudProfileVitalsStatus };
}
